import { AuthUserSession } from '@lib/types/authUser.types';
import { CASTING_ROLE_LIST } from '@lib/utils/constants';

export type ClaritySessionIdentity = {
  userId: number;
  impersonatedUserId: number | null;
  email: string | null;
};

/** Castifi id of the Cognito-authenticated user, never the impersonated one. */
const getSessionUserId = (session?: AuthUserSession | null): number | null => {
  const rawId =
    session?.attributes?.['custom:castifi_user_id'] ??
    session?.signInUserSession?.idToken?.payload?.['custom:castifi_user_id'];
  const userId = rawId != null ? Number(rawId) : Number.NaN;

  return Number.isFinite(userId) ? userId : null;
};

const toFiniteUserId = (
  value: number | string | null | undefined
): number | null => {
  if (value == null || value === '') return null;

  const userId = Number(value);

  return Number.isFinite(userId) ? userId : null;
};

const toNonEmptyEmail = (value: unknown): string | null => {
  if (typeof value !== 'string' || value === '') return null;

  return value;
};

/**
 * Resolves the session user's email only. Prefer Cognito attributes (always the
 * person at the keyboard); fall back to Redux profile.email only when that
 * profile belongs to the session user — never the impersonated target.
 */
export function resolveSessionUserEmail(
  session: AuthUserSession | null,
  profile: UserDetail | null | undefined,
  sessionUserId: number
): string | null {
  const sessionEmail = toNonEmptyEmail(session?.attributes?.email);

  if (sessionEmail != null) return sessionEmail;

  if (profile != null && Number(profile.id) === sessionUserId) {
    return toNonEmptyEmail(profile.email);
  }

  return null;
}

/**
 * Resolves who Clarity records under, or null when recording must not run.
 *
 * Sessions are always attributed to the Cognito-authenticated user — the person
 * at the keyboard. During impersonation, Redux may hold an impersonated target;
 * recording continues under the session user and the target is returned
 * separately for tagging when an explicit impersonation signal exists.
 *
 * Casting-role authorization is checked only against the session user's record.
 * When that record is still loading, a trusted impersonation signal alone is
 * enough to proceed because only authorized admins can reach that state.
 */
export function resolveClaritySessionIdentity(
  session: AuthUserSession | null,
  profile: UserDetail | null | undefined,
  impersonatedUserId: number | null = null
): ClaritySessionIdentity | null {
  const sessionUserId = getSessionUserId(session);

  if (sessionUserId == null) return null;

  const trustedImpersonatedId = toFiniteUserId(impersonatedUserId);

  const email = resolveSessionUserEmail(session, profile, sessionUserId);

  if (profile != null && Number(profile.id) === sessionUserId) {
    if (
      profile.casting_role == null ||
      !CASTING_ROLE_LIST.includes(profile.casting_role)
    ) {
      return null;
    }

    return {
      userId: sessionUserId,
      impersonatedUserId: trustedImpersonatedId,
      email,
    };
  }

  // Impersonating: session user already passed login; profile may still be loading.
  if (trustedImpersonatedId != null) {
    return {
      userId: sessionUserId,
      impersonatedUserId: trustedImpersonatedId,
      email,
    };
  }

  // Wait for the authorized session-user record before normal recording starts.
  return null;
}

/**
 * Resolves the user Clarity attributes sessions to, or null when recording
 * must not run. Prefer {@link resolveClaritySessionIdentity} when callers also
 * need the impersonated target.
 */
export function resolveClarityUserId(
  session: AuthUserSession | null,
  profile: UserDetail | null | undefined,
  impersonatedUserId: number | null = null
): number | null {
  return (
    resolveClaritySessionIdentity(session, profile, impersonatedUserId)
      ?.userId ?? null
  );
}
